<template>
  <div class="login-wrapper">
    <div class="login">
      <h1>動画共有システム</h1>
      <form class="login-form" @submit.prevent="tryLogin">
        <div class="form-group group1">
          <input
            type="text"
            class="form-control"
            ref="refTextInputUsername"
            placeholder="ログインID"
            v-model="state.loginForm.username"
          >
          <input
            type="password"
            class="form-control"
            ref="refTextInputPassword"
            placeholder="パスワード"
            v-model="state.loginForm.password"
          >
          <div v-if="state.errorMessage" class="text-danger text-center">
            <label class="small">{{ state.errorMessage }}</label>
          </div>
        </div>
        <button type="submit" class="btn btn-primary btn-lg">
          <i class="fas fa-sign-in-alt"></i>
          ログイン
        </button>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  reactive,
  useTemplateRef,
} from 'vue';
import axios from 'axios';
import { clearSession, setToken } from 'src/services/session';
import { useAuth } from 'src/composables/useAuth';

const CREDENTIAL_ERROR_MESSAGE = 'ログインIDまたはパスワードが間違っています';
const UNEXPECTED_ERROR_MESSAGE = 'ログインに失敗しました。時間をおいて再度お試しください';

// 認証情報の誤りとしてユーザーに提示するステータス（バックエンドは401を返すが、
// バリデーションエラー化された場合に備えて422も同様に扱う）
const CREDENTIAL_ERROR_STATUSES = [401, 422];

type LoginErrorMessage =
  | typeof CREDENTIAL_ERROR_MESSAGE
  | typeof UNEXPECTED_ERROR_MESSAGE
  | null;

interface LoginState {
 loginForm: {
    username: string;
    password: string;
  };
  errorMessage: LoginErrorMessage;
}

const state = reactive<LoginState>({
  loginForm: {
    username: '',
    password: '',
  },
  errorMessage: null,
});
// methods
// iOSのchromeだとautofillでchangeが効かないぽいのでがんばる
const refTextInputUsername = useTemplateRef<HTMLInputElement>('refTextInputUsername');
const refTextInputPassword = useTemplateRef<HTMLInputElement>('refTextInputPassword');
const ensureLoginFormValuesSynced = () => {
  const username = refTextInputUsername.value?.value;
  const password = refTextInputPassword.value?.value;
  state.loginForm.username = username || '';
  state.loginForm.password = password || '';
};
const auth = useAuth();

const tryLogin = async(): Promise<void> => {
  state.errorMessage = null;
  ensureLoginFormValuesSynced();

  // loginしようとするときは明示的に消す.
  // expiredなトークンを送り続けるとログインできないので.
  clearSession();

  try {
    const response = await auth.login(state.loginForm);
    if ('twoFactorUrl' in response) {
      location.href = response.twoFactorUrl + '&subsystem_name=movie-pf';
    } else if ('accessToken' in response) {
      setToken(response.accessToken);
      location.href = '/';
    }
  } catch (e) {
    const status = axios.isAxiosError(e) ? e.response?.status : null;
    if (status && CREDENTIAL_ERROR_STATUSES.includes(status)) {
      state.errorMessage = CREDENTIAL_ERROR_MESSAGE;
      return;
    }
    // ネットワークエラーや5xxなど想定外の失敗はログを残し、汎用メッセージを表示する
    console.error('login failed', e);
    state.errorMessage = UNEXPECTED_ERROR_MESSAGE;
  }
};
</script>

<style lang="scss" scoped>
.login-wrapper {
  height: 100%;
  width: 100%;
  background-color: #fafafa;
  padding-top: 60px;
}
.login {
  text-align: center;
}
.login-form {
  margin: 20px 0 0 0;
  .form-group.group1 {
    width: 240px;
    margin: 0 auto;
  }
}
.form-control {
  margin: 0 0 8px 0;
}
</style>
